import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { Medal, Trophy, Star } from "lucide-react";
import { IMAGES } from "@/data/site";
import { MEDAGLIERE_INTRO, MEDAGLIERE_SECTIONS } from "@/data/medagliere";

const medalTone = (result) => {
  const r = result.toLowerCase();
  if (r.startsWith("oro")) return "text-gold";
  if (r.startsWith("bronzo")) return "text-[#B08D57]";
  if (r.startsWith("vice") || r.includes("argento")) return "text-slate-400";
  if (r.startsWith("mondiali") || r.includes("mondo") || r.startsWith("europei")) return "text-cab-light";
  return "text-cab";
};

const AthleteCard = ({ athlete }) => (
  <div className="h-full rounded-2xl border border-ink/10 bg-white p-6 md:p-7">
    <div className="flex flex-wrap items-center gap-3">
      <h3 className="font-display text-lg font-extrabold tracking-tight text-ink md:text-xl">
        {athlete.name}
      </h3>
      {athlete.azzurro && (
        <span className="inline-flex items-center gap-1 rounded-full bg-cab/10 px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.15em] text-cab">
          <Star className="h-3 w-3" /> Atleta Azzurro
        </span>
      )}
    </div>
    <ul className="mt-4 space-y-2.5">
      {athlete.results.map((res, i) => (
        <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-muted-foreground">
          <Medal className={`mt-0.5 h-4 w-4 shrink-0 ${medalTone(res)}`} />
          <span>{res}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Medagliere() {
  return (
    <div data-testid="medagliere-page">
      <PageHero
        image={IMAGES.medagliereBg}
        overline="Palmarès · dal 1948"
        titleLines={["Il nostro", "medagliere"]}
        subtitle={MEDAGLIERE_INTRO}
      />

      {MEDAGLIERE_SECTIONS.map((section, idx) => (
        <section
          key={section.id}
          data-testid={`medagliere-${section.id}`}
          className={`py-20 md:py-28 ${idx % 2 === 0 ? "bg-cream" : "bg-white"}`}
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-8">
            <SectionHeading overline={section.kicker} title={section.title} />

            {section.athletes && (
              <Stagger className="mt-12 grid gap-5 md:mt-16 md:grid-cols-2 lg:grid-cols-3">
                {section.athletes.map((a, i) => (
                  <StaggerItem key={`${a.name}-${i}`}>
                    <AthleteCard athlete={a} />
                  </StaggerItem>
                ))}
              </Stagger>
            )}

            {section.team && (
              <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 md:mt-16">
                {section.team.map((t) => (
                  <StaggerItem key={t}>
                    <div className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-white p-6">
                      <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                        <Trophy className="h-6 w-6" />
                      </span>
                      <p className="font-display text-lg font-bold tracking-tight text-ink">{t}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
